import { Copy, Timer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const AREA = {
  lesson: "Opened a paid lesson", article: "Read a paid article", quiz: "Opened a paid quiz",
  video_library: "Watched in the Video Library", discord: "Linked Discord", download: "Downloaded a file",
  "ai-coach": "Used the Professor", journal: "Used the Journal", grader: "Used the Grader", climb: "Used The Climb",
  playbooks: "Used Playbooks", calculator: "Opened the Calculator", analytics: "Opened Analytics", content: "Opened paid content",
};
const STATUS = {
  open: ["Open — refund still possible", "bg-emerald-50 text-emerald-800 border-emerald-200"],
  closed_by_use: ["Closed — paid content used", "bg-amber-50 text-amber-800 border-amber-200"],
  expired: ["Closed — 24 hours passed", "bg-slate-100 text-slate-700 border-slate-200"],
  refunded: ["Refunded", "bg-blue-50 text-blue-800 border-blue-200"],
  no_purchase: ["No purchase yet", "bg-slate-50 text-slate-500 border-slate-200"],
};
const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");
const label = (a) => AREA[a] || a;

const evidenceText = (rw, user) => [
  `Member: ${user.name} <${user.email}>`,
  `Purchase: ${fmt(rw.purchase_at)} (Stripe session ${rw.session_id || "—"})`,
  `Refund window: 24h, closes ${fmt(rw.closes_at)} or on first use of paid content (signed agreement).`,
  rw.first_use && `First use of paid content: ${fmt(rw.first_use.at)} — ${label(rw.first_use.area)}${rw.first_use.item ? ` "${rw.first_use.item}"` : ""} — ${rw.first_use.minutes_after_purchase} min after purchase — IP ${rw.first_use.ip || "—"} — ${rw.first_use.ua || ""}`,
  rw.events?.length && "Usage log:",
  ...(rw.events || []).map((e) => `- ${label(e.area)}${e.item ? ` "${e.item}"` : ""}: first ${fmt(e.first_at)}, last ${fmt(e.last_at)}, ${e.count}x, IP ${e.ip || "—"}`),
].filter(Boolean).join("\n");

export const RefundWindowLog = ({ rw, user }) => {
  if (!rw) return null;
  const [text, cls] = STATUS[rw.status] || STATUS.no_purchase;
  const copy = () => navigator.clipboard.writeText(evidenceText(rw, user)).then(() => toast.success("Evidence copied"));
  return (
    <div data-testid="admin-refund-window" className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <span data-testid="admin-refund-window-status" className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${cls}`}>
          <Timer size={12} /> {text}
        </span>
        {rw.status !== "no_purchase" && (
          <Button size="sm" variant="outline" onClick={copy} data-testid="admin-refund-window-copy" className="ml-auto gap-1.5">
            <Copy size={13} /> Copy evidence
          </Button>
        )}
      </div>
      {rw.status !== "no_purchase" && (
        <div className="grid sm:grid-cols-2 gap-2 text-sm">
          <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500">Purchased</div><div className="font-medium">{fmt(rw.purchase_at)}</div></div>
          <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500">24h window ends</div><div className="font-medium">{fmt(rw.closes_at)}</div></div>
        </div>
      )}
      {rw.first_use && (
        <div data-testid="admin-refund-window-first-use" className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-sm">
          <div className="font-semibold text-amber-900">{label(rw.first_use.area)}{rw.first_use.item ? ` · ${rw.first_use.item}` : ""}</div>
          <div className="text-xs text-amber-800 mt-1">
            {fmt(rw.first_use.at)}
            {rw.first_use.minutes_after_purchase != null && ` · ${rw.first_use.minutes_after_purchase} min after purchase`}
            {rw.first_use.ip && ` · IP ${rw.first_use.ip}`}
          </div>
          {rw.first_use.ua && <div className="text-[11px] text-amber-700/80 mt-1 truncate">{rw.first_use.ua}</div>}
        </div>
      )}
      {rw.events?.length > 0 && (
        <ul data-testid="admin-refund-window-events" className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm">
          {rw.events.map((e, i) => (
            <li key={i} className="flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-slate-800">{label(e.area)}{e.item ? ` · ${e.item}` : ""}</div>
                <div className="text-xs text-slate-500">first {fmt(e.first_at)} · last {fmt(e.last_at)}</div>
              </div>
              <span className="text-xs font-mono text-slate-500">{e.count}×</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
